import { FC, ReactNode, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import {
  CloudDownloadIcon,
  EmptyDocumentIcon,
  UploadedFileIcon,
} from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { useFormat } from '../../../hooks/index';
import { getS3FileOriginalName } from '../../../utils/get-s3file-original-name.util';
import { isDocusignFileLink } from '../event-form/utils/resolve-linked-signature-file-links.util';

export type FilesModalProps = {
  attachedFilesLabel?: ReactNode;
  createdAt?: Date;
  docusignFileLinks?: string[];
  filesLinks: string[];
  name: ReactNode | string;
  onClose: () => void;
  onDownloadFile: (file: string) => void;
  onSuccess: () => void;
  stakeholderName?: string;
};

const orderFilesForModal = (filesLinks: string[], docusignFileLinks: string[]): string[] => {
  if (docusignFileLinks.length === 0) return filesLinks;

  const manualLinks = filesLinks.filter((link) => !isDocusignFileLink(link, docusignFileLinks));
  const docusignLinksInFiles = filesLinks.filter((link) =>
    isDocusignFileLink(link, docusignFileLinks),
  );
  const extraDocusignLinks = docusignFileLinks.filter(
    (link) => !isDocusignFileLink(link, filesLinks),
  );

  return [...manualLinks, ...Array.from(new Set([...docusignLinksInFiles, ...extraDocusignLinks]))];
};

export const FilesModal: FC<FilesModalProps> = ({
  attachedFilesLabel,
  onClose,
  onSuccess,
  onDownloadFile,
  filesLinks,
  docusignFileLinks = [],
  name,
  createdAt,
  stakeholderName,
}) => {
  const { format } = useFormat();
  const orderedFiles = useMemo(
    () => orderFilesForModal(filesLinks, docusignFileLinks),
    [docusignFileLinks, filesLinks],
  );
  const attachedFilesCount = orderedFiles.length;

  return (
    <div className="m-2 flex max-w-[358px] flex-1 flex-col gap-8 rounded bg-white p-4 xs:max-w-[453px]">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2 border-b-[1px] border-gray-100 pb-4">
          <span className="max-w-[358px] truncate text-xl font-semibold text-gray-700">
            {stakeholderName || <AppFormattedMessage id="FILES" />}
          </span>
          <span className="text-xs font-[450] text-gray-800">
            {name}
            {createdAt && ` - ${format(createdAt, 'dd/MM/yyyy')}`}
          </span>
        </div>
        <div className="flex flex-col gap-4">
          <span className="text-xs font-[450] text-gray-500">
            {attachedFilesLabel ?? (
              <AppFormattedMessage id="ATTACHED_FILES" values={{ count: attachedFilesCount }} />
            )}
          </span>
          <div className="flex flex-col gap-2">
            {orderedFiles.map((file) => {
              const isDocusign = isDocusignFileLink(file, docusignFileLinks);

              return (
                <div
                  className={twMerge(
                    'flex w-full items-center justify-between gap-2 rounded border-[1px] p-3',
                    isDocusign ? 'border-[#DDC0F9] bg-[#F0E5FA]' : 'border-brand-50 bg-brand-25',
                  )}
                  key={file}
                >
                  <button
                    className="flex min-w-0 cursor-pointer items-center gap-2 overflow-hidden border-0 bg-transparent p-0 text-left"
                    onClick={() => onDownloadFile(file)}
                    type="button"
                  >
                    {isDocusign ? (
                      <UploadedFileIcon iconColor="#6500CA" />
                    ) : (
                      <EmptyDocumentIcon className="size-4" iconColor="#2565C8" />
                    )}
                    <span className="truncate text-xs font-[450] text-brand-700">
                      {getS3FileOriginalName(file)}
                    </span>
                  </button>
                  <CloudDownloadIcon
                    className="size-4 shrink-0 cursor-pointer"
                    onClick={() => onDownloadFile(file)}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="flex h-11 w-full justify-end gap-3">
        <Button
          className="h-full w-fit rounded border border-gray-300 px-6 py-[10px] text-sm font-[450] text-gray-700"
          onClick={onClose}
          styleType="NONE"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <Button
          className="h-full w-fit bg-brand-700 px-6 py-[10px] text-sm font-[550] text-white"
          onClick={onSuccess}
          styleType="NONE"
        >
          <AppFormattedMessage id="DOWNLOAD_ALL" />
        </Button>
      </div>
    </div>
  );
};
